// utils/exportUtils.js
import * as XLSX from 'xlsx';
import surveyApi from '../services/surveyApi';

/**
 * Formats date to YYYY-MM-DD format
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
const formatDate = (date) => {
  return date.toISOString().split('T')[0];
};

/**
 * Gets date range parameters based on selected range
 * @param {string} dateRange - Selected date range (last7, last30, last90, thisYear, allTime)
 * @returns {Object} Object with start and end dates
 */
const getDateRangeParams = (dateRange) => {
  const endDate = new Date();
  let startDate = new Date();
  
  switch (dateRange) {
    case 'last7':
      startDate.setDate(endDate.getDate() - 7);
      break;
    case 'last30':
      startDate.setDate(endDate.getDate() - 30);
      break;
    case 'last90':
      startDate.setDate(endDate.getDate() - 90);
      break;
    case 'thisYear':
      startDate = new Date(endDate.getFullYear(), 0, 1); // January 1st of current year
      break;
    case 'allTime':
      startDate = new Date(2000, 0, 1); // Far back enough to include all data
      break;
    default:
      startDate.setDate(endDate.getDate() - 30); // Default to last 30 days
  }
  
  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate)
  };
};

/**
 * Fetches survey data based on date range and exports to Excel
 * @param {string} dateRange - Selected date range
 * @param {Object} customDateRange - Optional custom date range {startDate, endDate}
 * @param {Function} onError - Callback for error handling
 */
export const exportSurveyData = async (dateRange, customDateRange = null, onError = () => {}) => {
  try {
    // Get date parameters
    const dateParams = customDateRange || getDateRangeParams(dateRange);
    
    // Fetch data with date parameters
    const response = await surveyApi.getSurveyWithDateRange(dateParams.startDate, dateParams.endDate);
    
    if (!response.data.success) {
      throw new Error('Failed to fetch data for export');
    }
    
    // Extract data from response
    const data = response.data.data.data || response.data.data || [];
    
    if (data.length === 0) {
      throw new Error('No data available for the selected date range');
    }
    
    // Format data for export
    const exportData = data.map(item => ({
      'Submission ID': item.id,
      'Date': new Date(item.created_at).toLocaleDateString(),
      'Time': new Date(item.created_at).toLocaleTimeString(),
      'Client Type': item.client_type || 'N/A',
      'Office/Section': item.office || 'N/A',
      'Service': item.service || 'N/A',
      'SQD0 Score': item.sqd0 || 'N/A',
      'SQD1 Score': item.sqd1 || 'N/A',
      'SQD2 Score': item.sqd2 || 'N/A',
      'SQD3 Score': item.sqd3 || 'N/A',
      'SQD4 Score': item.sqd4 || 'N/A', 
      'SQD5 Score': item.sqd5 || 'N/A',
      'SQD6 Score': item.sqd6 || 'N/A',
      'SQD7 Score': item.sqd7 || 'N/A',
      'SQD8 Score': item.sqd8 || 'N/A',
      'SQD9 Score': item.sqd9 || 'N/A',
      'SQD10 Score': item.sqd10 || 'N/A',
      'Comments': item.comment || ''
    }));
    
    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Survey Data');
    
    // Generate filename with date range
    let fileName = `SurveyData_${dateParams.startDate}_to_${dateParams.endDate}.xlsx`;
    
    // Export to file
    XLSX.writeFile(workbook, fileName);
    
    return { success: true };
  } catch (error) {
    console.error('Export error:', error);
    onError(error.message || 'Failed to export data');
    return { success: false, error: error.message };
  }
};

/**
 * Exports survey data summary and charts
 * @param {Object} reportData - Data for reports including charts
 * @param {Object} sqdScores - SQD scores for the report
 * @param {string} dateRange - Selected date range
 * @param {Function} onError - Callback for error handling
 */
export const exportReportData = (reportData, sqdScores, dateRange, onError = () => {}) => {
  try {
    const dateParams = getDateRangeParams(dateRange);
    
    // Create workbook
    const workbook = XLSX.utils.book_new();
    
    // Summary worksheet
    const summaryData = [
      { 'Report Type': 'Survey Data Summary' },
      { 'Date Range': `${dateParams.startDate} to ${dateParams.endDate}` },
      { 'Generated On': new Date().toLocaleString() },
      { 'SQD Overall Score': calculateOverallSQDScore(sqdScores) }
    ];
    
    const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData, { skipHeader: true });
    XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');
    
    // SQD Scores worksheet
    const sqdData = Object.keys(sqdScores).map(key => ({
      'SQD Dimension': key,
      'Score': sqdScores[key].toFixed(2)
    }));
    
    const sqdWorksheet = XLSX.utils.json_to_sheet(sqdData);
    XLSX.utils.book_append_sheet(workbook, sqdWorksheet, 'SQD Scores');
    
    // Generate filename
    let fileName = `SurveyReport_${dateParams.startDate}_to_${dateParams.endDate}.xlsx`;
    
    // Export to file
    XLSX.writeFile(workbook, fileName);
    
    return { success: true };
  } catch (error) {
    console.error('Export report error:', error);
    onError(error.message || 'Failed to export report');
    return { success: false, error: error.message };
  }
};

// Helper to calculate overall SQD score
const calculateOverallSQDScore = (sqdScores) => {
  const scores = Object.values(sqdScores);
  if (scores.length === 0) return 0;
  return scores.reduce((sum, score) => sum + score, 0) / scores.length;
};